/**
 * MobStatSheet
 *
 * Full monster-manual style stat block modal for a mob.
 * Read-only for now; the compact MobPanel handles live-combat edits.
 */

import { useEffect } from 'react';
import { getAbilityMod } from '../../stores/mobStore';
import type { Mob } from '../../stores/mobStore';

const GOLD = '#d4af37';
const GOLD_DIM = 'rgba(212, 175, 55, 0.3)';
const PARCHMENT = '#f4e4bc';
const DARK_BG = '#0a080c';
const BLOOD = '#7a1818';

interface MobStatSheetProps {
  mob: Mob;
  onClose: () => void;
}

const ABILITY_ORDER = ['strength', 'dexterity', 'constitution', 'intelligence', 'wisdom', 'charisma'] as const;
const ABILITY_LABEL: Record<string, string> = {
  strength: 'STR',
  dexterity: 'DEX',
  constitution: 'CON',
  intelligence: 'INT',
  wisdom: 'WIS',
  charisma: 'CHA',
};

function fmt(mod: number): string {
  return mod >= 0 ? `+${mod}` : `${mod}`;
}

function parseCsv(s: string | undefined | null): string[] {
  if (!s) return [];
  return s.split(',').map((v) => v.trim()).filter(Boolean);
}

export function MobStatSheet({ mob, onClose }: MobStatSheetProps) {
  // Escape to close
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const crDisplay = typeof mob.cr === 'number' && mob.cr > 0
    ? (mob.cr < 1 ? `1/${Math.round(1 / mob.cr)}` : String(mob.cr))
    : '—';

  const resistances = parseCsv(mob.damageResistances);
  const immunities = parseCsv(mob.damageImmunities);
  const vulnerabilities = parseCsv(mob.damageVulnerabilities);
  const conditionImmunities = parseCsv(mob.conditionImmunities);

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
        padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: DARK_BG,
          border: `2px solid ${GOLD}`,
          borderRadius: 6,
          maxWidth: 620,
          width: '100%',
          maxHeight: '90vh',
          overflowY: 'auto',
          color: PARCHMENT,
          fontFamily: 'Georgia, serif',
          boxShadow: `0 0 40px rgba(0, 0, 0, 0.9)`,
          position: 'relative',
        }}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          style={{
            position: 'absolute', top: 10, right: 14, zIndex: 1,
            background: 'transparent', border: 'none', color: GOLD_DIM,
            cursor: 'pointer', padding: 4, fontSize: 20, lineHeight: 1,
          }}
          aria-label="Close"
        >
          ×
        </button>

        {/* Header */}
        <div style={{ padding: '18px 20px 14px', borderBottom: `1px solid ${GOLD_DIM}` }}>
          <h2 style={{
            margin: 0, fontSize: 22, fontWeight: 700, color: BLOOD,
            fontFamily: 'Georgia, serif', fontVariant: 'small-caps', letterSpacing: 1,
          }}>
            {mob.displayName || mob.name}
          </h2>
          <div style={{ fontSize: 12, color: 'rgba(244, 228, 188, 0.7)', marginTop: 4, fontStyle: 'italic' }}>
            {mob.size || 'Medium'} {mob.type || 'creature'}
            {mob.alignment ? `, ${mob.alignment}` : ''}
          </div>
        </div>

        {/* Top stats block */}
        <div style={{ padding: '12px 20px', borderBottom: `1px solid ${GOLD_DIM}` }}>
          <StatRow label="Armor Class" value={String(mob.ac)} />
          <StatRow
            label="Hit Points"
            value={`${mob.currentHp} / ${mob.maxHp}${mob.tempHp > 0 ? ` (+${mob.tempHp} temp)` : ''}`}
          />
          <StatRow label="Speed" value={`${mob.speed} ft.`} />
          <StatRow label="Challenge" value={crDisplay} />
        </div>

        {/* Ability scores grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(6, 1fr)',
          borderBottom: `1px solid ${GOLD_DIM}`,
          padding: '10px 16px',
          gap: 4,
        }}>
          {ABILITY_ORDER.map((ability) => {
            const score = mob[ability];
            const mod = getAbilityMod(score);
            return (
              <div key={ability} style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: GOLD, letterSpacing: 0.5 }}>
                  {ABILITY_LABEL[ability]}
                </div>
                <div style={{ fontSize: 16, fontWeight: 600, marginTop: 2 }}>{score}</div>
                <div style={{ fontSize: 11, color: 'rgba(244, 228, 188, 0.7)' }}>({fmt(mod)})</div>
              </div>
            );
          })}
        </div>

        {/* Saving throws */}
        {ABILITY_ORDER.some((a) => mob.saves[a] !== undefined) && (
          <StatLine
            label="Saving Throws"
            value={
              ABILITY_ORDER
                .filter((a) => mob.saves[a] !== undefined)
                .map((a) => `${ABILITY_LABEL[a]} ${fmt(mob.saves[a]!)}`)
                .join(', ')
            }
          />
        )}

        {/* Damage adjustments */}
        {vulnerabilities.length > 0 && (
          <StatLine label="Vulnerabilities" value={vulnerabilities.join(', ')} />
        )}
        {resistances.length > 0 && (
          <StatLine label="Resistances" value={resistances.join(', ')} />
        )}
        {immunities.length > 0 && (
          <StatLine label="Damage Immunities" value={immunities.join(', ')} />
        )}
        {conditionImmunities.length > 0 && (
          <StatLine label="Condition Immunities" value={conditionImmunities.join(', ')} />
        )}

        {/* Active conditions */}
        {mob.conditions.length > 0 && (
          <StatLine
            label="Conditions (live)"
            value={mob.conditions.map((c) => c.type + (c.duration ? ` (${c.duration}r)` : '')).join(', ')}
          />
        )}

        {/* Actions */}
        {mob.actions.length > 0 && (
          <Section title="Actions">
            {mob.actions.map((action, i) => (
              <div key={i} style={{ marginBottom: 8 }}>
                <span style={{ fontWeight: 700, fontStyle: 'italic', color: PARCHMENT }}>
                  {action.name}.
                </span>{' '}
                <span style={{ fontStyle: 'italic', color: 'rgba(244, 228, 188, 0.85)' }}>
                  {action.range === 'melee' ? 'Melee' : 'Ranged'} Weapon Attack:
                </span>{' '}
                {fmt(action.attackBonus)} to hit
                {action.range === 'melee' ? ', reach 5 ft.' : ', range 30/120 ft.'}
                , one target.{' '}
                <span style={{ fontStyle: 'italic', color: 'rgba(244, 228, 188, 0.85)' }}>
                  Hit:
                </span>{' '}
                {action.damageDice} {action.damageType} damage.
                {action.description && (
                  <div style={{ marginTop: 4, fontSize: 13, color: 'rgba(244, 228, 188, 0.75)' }}>
                    {action.description}
                  </div>
                )}
              </div>
            ))}
          </Section>
        )}

        {/* GM behavior notes */}
        {mob.behaviorNotes && (
          <Section title="Behavior Notes">
            <div style={{ fontStyle: 'italic', fontSize: 13, color: 'rgba(244, 228, 188, 0.8)' }}>
              {mob.behaviorNotes}
            </div>
          </Section>
        )}

        {/* Footer metadata */}
        <div style={{
          padding: '8px 20px',
          borderTop: `1px solid ${GOLD_DIM}`,
          fontSize: 10,
          color: 'rgba(244, 228, 188, 0.4)',
          display: 'flex',
          justifyContent: 'space-between',
        }}>
          <span>{mob.open5eSlug ? `SRD: ${mob.open5eSlug}` : 'Custom'}</span>
          <span>ID: {mob.id.slice(0, 8)}…</span>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// SUB-COMPONENTS
// =============================================================================

function StatRow({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ fontSize: 13, marginBottom: 3 }}>
      <span style={{ fontWeight: 700, color: BLOOD }}>{label}</span>
      <span style={{ color: 'rgba(244, 228, 188, 0.9)' }}> {value}</span>
    </div>
  );
}

function StatLine({ label, value }: { label: string; value: string }) {
  return (
    <div style={{
      padding: '6px 20px',
      fontSize: 13,
      borderBottom: `1px solid ${GOLD_DIM}`,
    }}>
      <span style={{ fontWeight: 700, fontStyle: 'italic', color: BLOOD }}>{label}</span>
      <span style={{ color: 'rgba(244, 228, 188, 0.85)' }}> {value}</span>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ padding: '10px 20px 12px', borderBottom: `1px solid ${GOLD_DIM}` }}>
      <h3 style={{
        fontSize: 15, margin: '0 0 8px 0',
        fontWeight: 700, color: BLOOD,
        fontVariant: 'small-caps', letterSpacing: 0.5,
        borderBottom: `1px solid ${BLOOD}`,
        paddingBottom: 2,
      }}>
        {title}
      </h3>
      <div style={{ fontSize: 13, color: 'rgba(244, 228, 188, 0.9)', lineHeight: 1.5 }}>
        {children}
      </div>
    </div>
  );
}
